import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function MaintenanceContractsPage() {
  useEffect(() => {
    document.title = 'Electrical Maintenance Contracts Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Electrical maintenance contracts in Watford. Regular inspections, testing & preventative maintenance for homes & businesses. NICEIC approved. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Electrical maintenance contracts in Watford. Regular inspections, testing & preventative maintenance for homes & businesses. NICEIC approved. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/repairs/maintenance-contracts",
      "telephone": "02031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday"
        ],
        "opens": "00:00",
        "closes": "23:59"
      },
      "serviceType": "Electrical Maintenance Contracts",
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      emoji: '🔍',
      name: 'Regular Inspections',
      description: 'Scheduled electrical inspections to identify potential issues before they become costly problems or safety hazards.'
    },
    {
      emoji: '🔧',
      name: 'Preventive Maintenance',
      description: 'Proactive maintenance including testing, cleaning, and adjusting electrical systems to ensure optimal performance.'
    },
    {
      emoji: '📋',
      name: 'Compliance Testing',
      description: 'Regular testing to ensure your electrical installation continues to meet BS 7671 wiring regulations and safety standards.'
    },
    {
      emoji: '⚡',
      name: 'Priority Call-Outs',
      description: 'Fast-track emergency response for contract customers with guaranteed priority scheduling and reduced rates.'
    },
    {
      emoji: '📊',
      name: 'Detailed Reporting',
      description: 'Comprehensive maintenance reports documenting all work completed, test results, and recommendations for improvements.'
    },
    {
      emoji: '💰',
      name: 'Cost Savings',
      description: 'Reduced call-out fees, discounted labor rates, and prevention of expensive emergency repairs through regular maintenance.'
    }
  ];

  const contractBenefits = [
    {
      emoji: '🛡️',
      benefit: 'Peace of Mind',
      description: 'Regular professional maintenance ensures your electrical systems are safe, compliant, and operating efficiently.'
    },
    {
      emoji: '💸',
      benefit: 'Reduced Costs',
      description: 'Prevent expensive emergency repairs and benefit from discounted rates on all maintenance and repair work.'
    },
    {
      emoji: '⚡',
      benefit: 'Priority Service',
      description: 'Jump the queue with priority scheduling for routine maintenance and emergency call-outs when you need them.'
    },
    {
      emoji: '📅',
      benefit: 'Scheduled Visits',
      description: 'Pre-planned maintenance visits at times convenient to you, ensuring your systems are always in top condition.'
    },
    {
      emoji: '✅',
      benefit: 'Compliance Assured',
      description: 'Stay compliant with electrical regulations and insurance requirements through regular professional inspections.'
    },
    {
      emoji: '🔋',
      benefit: 'Extended Lifespan',
      description: 'Regular maintenance extends the life of your electrical equipment and reduces the risk of unexpected failures.'
    }
  ];

  const contractTypes = [
    {
      type: 'Residential',
      icon: '🏠',
      description: 'Perfect for homeowners who want peace of mind and priority service',
      features: [
        'Annual electrical inspection',
        'Priority emergency call-outs',
        '15% discount on all repairs',
        'Free safety advice and recommendations',
        'Compliance certification included'
      ]
    },
    {
      type: 'Commercial',
      icon: '🏢',
      description: 'Ideal for businesses requiring regular maintenance and compliance',
      features: [
        'Quarterly electrical inspections',
        '24/7 priority emergency response',
        '20% discount on all work',
        'Detailed maintenance reports',
        'PAT testing included',
        'Compliance documentation'
      ]
    },
    {
      type: 'Landlord',
      icon: '🔑',
      description: 'Essential for landlords managing multiple rental properties',
      features: [
        'EICR testing every 5 years',
        'Annual safety inspections',
        'Priority tenant call-outs',
        '15% discount on repairs',
        'Compliance certificates',
        'Multi-property discounts'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/b3542454cb27e7c8d1a63185692fe59d.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Approved
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Serving Watford
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-calendar-check-fill"></i>
              Flexible Contracts
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Electrical Maintenance Contracts<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Protect your property and save money with our electrical maintenance contracts in Watford, Rickmansworth, and surrounding areas. Regular inspections, priority service, and peace of mind for homes, businesses, and landlords.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Get a Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              What's Included in Our Maintenance Contracts
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive electrical maintenance services to keep your property safe and compliant
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-xl text-3xl mb-4">
                  {service.emoji}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contract Types */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Choose Your Maintenance Contract
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Flexible maintenance contracts tailored to your specific needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {contractTypes.map((contract, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-xl border-2 border-gray-200 hover:border-[#FFD700] transition-all duration-300"
              >
                <div className="text-center mb-6">
                  <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-full text-4xl mx-auto mb-4">
                    {contract.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-[#0A1628] mb-2">
                    {contract.type}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {contract.description}
                  </p>
                </div>
                <ul className="space-y-3">
                  {contract.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-fill text-[#FFD700] text-xl flex-shrink-0 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="/#contact"
                  className="mt-6 w-full bg-[#FFD700] text-[#0A1628] px-6 py-3 rounded-full font-bold text-center hover:bg-[#FFC700] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
                >
                  Get Started
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Benefits of a Maintenance Contract
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why regular electrical maintenance makes financial and safety sense
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contractBenefits.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-lg text-2xl mb-4">
                  {item.emoji}
                </div>
                <h3 className="text-lg font-bold text-[#0A1628] mb-2">
                  {item.benefit}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Your trusted electrical maintenance partner in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                📅
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Flexible Scheduling</h3>
              <p className="text-gray-300 leading-relaxed">
                Maintenance visits scheduled at times convenient to you with minimal disruption
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                ✅
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">NICEIC Certified</h3>
              <p className="text-gray-300 leading-relaxed">
                All maintenance carried out by fully qualified electricians to the highest standards
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#FFD700] rounded-full text-3xl mx-auto mb-4">
                💰
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Cost Effective</h3>
              <p className="text-gray-300 leading-relaxed">
                Save money with discounted rates and prevent expensive emergency repairs
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">200+</div>
              <div className="text-gray-300 text-sm">Active Contracts</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">100%</div>
              <div className="text-gray-300 text-sm">Compliance Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">20%</div>
              <div className="text-gray-300 text-sm">Average Savings</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-[#FFD700] mb-2">15+</div>
              <div className="text-gray-300 text-sm">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Areas We Cover
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Electrical maintenance contracts available throughout Watford, Rickmansworth, and surrounding areas including:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Start Your Maintenance Contract?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact us today to discuss your electrical maintenance needs and get a tailored contract quote for your Watford property.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Request a Quote
            </a>
          </div>
          <div className="mt-8 flex items-center justify-center gap-2 text-[#FFD700] text-sm font-semibold">
            <i className="ri-calendar-check-fill"></i>
            Flexible Contracts Available
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}